import { useMemo, useState } from 'react';
import { FileText, Plus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button, IconButton, Card, CardBody, Skeleton,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useCommissionPolicies,
  useDeleteCommissionPolicy,
  type CommissionPolicy,
  type CommissionPolicyListParams,
} from '@/api/commission-policies';
import { formatDate } from '@/lib/format';
import { useAffiliatePolicyPermissions } from '../use-affiliate-policy-permissions';
import { PolicyFormDrawer } from './policy-form-drawer';

const PAGE_SIZE = 20;

/** Scope label shown in the table — mirrors the legacy COMMISSION_POLICY_SCOPE_OPTIONS. */
function scopeLabel(scope: CommissionPolicy['scope']): string {
  switch (scope) {
    case 'product_group': return 'Product Group';
    case 'product': return 'Product';
    default: return 'Default';
  }
}

/** Audience column text — shows referrer_type + department count if employee-scoped. */
function audienceLabel(policy: CommissionPolicy): string {
  if (!policy.referrer_type) return 'Everyone';
  if (policy.referrer_type === 'employee') {
    const deptCount = policy.department_ids?.length ?? 0;
    return deptCount > 0 ? `Employee (${deptCount} dept)` : 'Employee';
  }
  return policy.referrer_type.charAt(0).toUpperCase() + policy.referrer_type.slice(1);
}

function scopeTone(scope: CommissionPolicy['scope']): 'neutral' | 'warning' | 'info' {
  switch (scope) {
    case 'product_group': return 'warning';
    case 'product': return 'info';
    default: return 'neutral';
  }
}

/**
 * Commission Policies manager — server-paginated list of commission policies with
 * create/edit (Drawer) and delete (ConfirmDialog). Gated by `useAffiliatePolicyPermissions`.
 */
export default function CommissionPoliciesPage() {
  const toast = useToast();
  const perms = useAffiliatePolicyPermissions();

  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<CommissionPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CommissionPolicy | null>(null);

  const params: CommissionPolicyListParams = {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    owner_type: 'all',
  };

  const { data, isLoading, isError, refetch } = useCommissionPolicies(params);
  const rows: CommissionPolicy[] = data?.results ?? [];
  const total: number = data?.pagination?.total ?? rows.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* Delete mutation — re-created per target so the policy_id is baked in. */
  const deleteMutation = useDeleteCommissionPolicy(deleteTarget?.policy_id ?? 0);

  const openCreate = () => {
    setEditingPolicy(null);
    setDrawerOpen(true);
  };

  const openEdit = (policy: CommissionPolicy) => {
    setEditingPolicy(policy);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) setEditingPolicy(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync();
      toast.show({ tone: 'success', title: 'Policy deleted', description: deleteTarget.policy_name });
      setDeleteTarget(null);
    } catch (err) {
      toast.show({
        tone: 'danger',
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const columns = useMemo<ColumnDef<CommissionPolicy, unknown>[]>(
    () => [
      {
        id: 'policy_name',
        header: 'Policy Name',
        cell: ({ row }) => row.original.policy_name,
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: ({ row }) => (
          <StatusPill tone={scopeTone(row.original.scope)}>
            {scopeLabel(row.original.scope)}
          </StatusPill>
        ),
      },
      {
        id: 'product',
        header: 'Product / Group',
        cell: ({ row }) => {
          const p = row.original;
          if (p.scope === 'product_group') return p.product_group_name ?? `Group #${p.product_group_id}`;
          if (p.scope === 'product') return p.product_name ?? `Product #${p.product_id}`;
          return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
        },
      },
      {
        id: 'audience',
        header: 'Audience',
        cell: ({ row }) => audienceLabel(row.original),
      },
      {
        id: 'commission_percent',
        header: 'Commission %',
        cell: ({ row }) => `${row.original.commission_percent}%`,
      },
      {
        id: 'priority',
        header: 'Priority',
        cell: ({ row }) => row.original.priority,
      },
      {
        id: 'effective_date',
        header: 'Effective',
        cell: ({ row }) => formatDate(row.original.effective_date),
      },
      {
        id: 'expired_date',
        header: 'Expires',
        cell: ({ row }) => row.original.expired_date ? formatDate(row.original.expired_date) : '—',
      },
      {
        id: 'is_active',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={row.original.is_active ? 'success' : 'neutral'}>
            {row.original.is_active ? 'Active' : 'Inactive'}
          </StatusPill>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const policy = row.original;
          if (!perms.canUpdatePolicy && !perms.canDeletePolicy) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Policy actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {perms.canUpdatePolicy && (
                  <DropdownMenuItem onSelect={() => openEdit(policy)}>
                    <Pencil size={14} />
                    Edit
                  </DropdownMenuItem>
                )}
                {perms.canDeletePolicy && (
                  <DropdownMenuItem
                    onSelect={() => setDeleteTarget(policy)}
                    tone="danger"
                  >
                    <Trash2 size={14} />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [perms.canUpdatePolicy, perms.canDeletePolicy],
  );

  return (
    <Container size="full">
      <PageHeader
        title="Commission Policies"
        description="Define referral commission rules by scope, audience, and product."
        actions={
          perms.canCreatePolicy ? (
            <Button startIcon={<Plus size={15} />} onClick={openCreate}>
              New policy
            </Button>
          ) : null
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load commission policies"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.policy_id)}
          emptyState={
            <EmptyState
              icon={<FileText size={24} />}
              title="No commission policies"
              description="Create your first policy to define commission rules for affiliates."
            />
          }
          footer={
            <Pagination
              page={page}
              pageCount={pageCount}
              onPageChange={setPage}
              summary={`${rows.length} of ${total} policies`}
            />
          }
        />
      )}

      <PolicyFormDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        policy={editingPolicy}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete commission policy"
        description={`Are you sure you want to delete "${deleteTarget?.policy_name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
}
